import {Injectable, OnDestroy} from '@angular/core';
import {FaultOperationPathEnum} from '@docma-mm-core/fault/patch/fault-operation-path.enum';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {FaultPatchService} from '@docma-mm-core/fault/patch/fault-patch-service';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {SyncFileService} from '@docma-mm-core/sync/sync-file.service';
import {ConnectivityService} from '@mydocma/core';
import {AttachmentData} from './attachment';
import {AttachmentApiService} from './attachment-api.service';
import {mapFileEntityToFile} from './attachment-db.entity';
import {AttachmentDbService} from './attachment-db.service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {getError} from '@docma-mm-core/utils/utils';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {MmTranslateService} from '../../mm-translate.service';

@Injectable()
export class AttachmentSyncService implements OnDestroy {


  constructor(private readonly connectivityService: ConnectivityService,
              private readonly attachmentApiService: AttachmentApiService,
              private readonly patchService: FaultPatchService,
              private readonly syncFileService: SyncFileService,
              private readonly dbService: AttachmentDbService,
              private readonly notifactionService: NotificationService,
              private readonly translateService: MmTranslateService)  {
  }
  ngOnDestroy(): void {

  }


  public async syncFaultAttachmentFromPatches(mydocmaUid: string, faultId: any, patches: FaultPatch[]): Promise<SyncResult> {
    this.assertValidPatches(patches, FaultOperationPathEnum.attachment);
    const attachmentUid = patches[0].oUid;
    const faultUid = patches[0].fUid;
    let attachmentChangeState: ChangeState<any>;
    let loadedState;

    console.debug(`Syc fault attachment ${faultUid}/${attachmentUid}: Starting`);

    try {
      await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Syncing }));
       attachmentChangeState = this.patchService.generateChangeStateFromPatches(patches);
      if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
        let newFile: File;
        if (attachmentChangeState.status === ChangeStatusEnum.New) {
          newFile = await this.getAttachmentFile(mydocmaUid, attachmentUid);
          if (!newFile) {
            // NO FILE EXISTING ANYMORE; REMOVE FAULT_PATCH
            await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

            const m = `Can't sync new attachment ${attachmentUid}. Sync file not found`;
            console.error(m);
            throw new Error(m);
          }
        }

        loadedState = await this.syncFaultAttachmentChangeState(mydocmaUid, newFile, faultId, (patches[0].oId ?? patches[0].oUid), attachmentChangeState);
        await this.dbService.saveLoadState(mydocmaUid, faultUid, null, null, attachmentUid, loadedState);
      }
      await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

      const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Synced }));
      return new SyncResult({syncState, attachmentsLoadStates: [loadedState] });
    } catch (err) {
      console.error(err);

      if (err.error === 'Unique constraint violation of \'UQ_FI_UNIQUE_ID\'') {
        // ATTACHMENT ALREADY UPLOADED, REMOVE FAULT_PATCH + FILE
        try {
          await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
          await this.syncFileService.removeFile(mydocmaUid, attachmentUid);
          return new SyncResult({syncState: await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Synced }))});
        } catch (err2) {
          const error2Internal = getError(err2);
          console.error(error2Internal);
          return new SyncResult({syncState: await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: error2Internal }))});
        }
      }
      else {
        const errorInternal = getError(err);
        return new SyncResult({syncState: await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: errorInternal }))});
      }
    }
  }

  public async syncActivityAttachmentFromPatches(mydocmaUid: string, newActivityId: any, patches: FaultPatch[]): Promise<({ syncResult: SyncResult, isNewAttachment: boolean })> {
    this.assertValidPatches(patches, FaultOperationPathEnum.activityAttachment);
    const attachmentId = patches[0].oId || patches[0].oUid;
    const attachmentUid = patches[0].oUid;
    const activityId = (patches[0].pId || patches[0].pUid) || newActivityId;
    const activityUid = patches[0].pUid;
    const faultUid = patches[0].fUid;
    let attachmentChangeState: ChangeState<any>;
    let loadedState: LoadState<AttachmentData>;
    let isNew: boolean = false;

    console.debug(`Syc activity attachment ${faultUid}/${attachmentId}: Starting`);

    try {
      await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentId, new SyncState({ status: SyncStatusEnum.Syncing }));
      attachmentChangeState = this.patchService.generateChangeStateFromPatches(patches);
      if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
        let newFile: File;
        if (attachmentChangeState.status === ChangeStatusEnum.New) {
          isNew = true;
          newFile = await this.getAttachmentFile(mydocmaUid, attachmentUid);
          if (!newFile) {
            // NO FILE EXISTING ANYMORE; REMOVE FAULT_PATCH
            await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

            const m = `Can't sync new attachment ${attachmentId}. Sync file not found`;
            console.error(m);
            throw new Error(m);
          }
        }

        loadedState = await this.syncActivityAttachmentChangeState(mydocmaUid, newFile, activityId, (patches[0].oId ?? patches[0].oUid), attachmentChangeState);
        await this.dbService.saveLoadState(mydocmaUid, faultUid, activityUid, null, attachmentUid, loadedState);
      }
      await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

      const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Synced }));
      return ({syncResult: new SyncResult({syncState, activityAttachmentsLoadStates: [loadedState]}), isNewAttachment: isNew});

    } catch (err) {
      console.error(err);

      if (err.error === 'Unique constraint violation of \'UQ_FI_UNIQUE_ID\'') {
        // ATTACHMENT ALREADY UPLOADED, REMOVE FAULT_PATCH + FILE
        try {
          await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
          await this.syncFileService.removeFile(mydocmaUid, attachmentUid);

          const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Synced }));
          return ({syncResult: new SyncResult({syncState}), isNewAttachment: isNew });
        }
        catch (err2) {
          const error2Internal = getError(err2);
          console.error(error2Internal);

          const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: error2Internal}));
          return ({syncResult:  new SyncResult({syncState}), isNewAttachment: isNew });
        }
      }
      else if (err.error.Code === '1000017') { // Todo: add lock flag on Attachment to avoid error
        this.notifactionService.error(this.translateService.instant('MM.fault.lockedAttachment'));
        await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
        await this.syncFileService.removeFile(mydocmaUid, attachmentUid);
        return;
      }
      else {
        const errorInternal = getError(err);
        return ({syncResult: new SyncResult({syncState: await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: errorInternal}))}), isNewAttachment: isNew });
      }
    }
  }

  public async syncActivityGroupAttachmentFromPatches(mydocmaUid: string, groupUid: string, groupId: number, patches: {[p: string]: FaultPatch[]}, faultMap: Map<string, string>): Promise<SyncResult> {
    console.debug(`Syc activity group attachments`);
    const items: {faultUid: string, activityUid: string, attachmentUid: string}[] = [];
    const allPatches: FaultPatch[] = [];
    const syncResult = new SyncResult();

    for (const key of Object.keys(patches)) {
      const attachmentPatches: FaultPatch[] = patches[key];
      allPatches.push(... attachmentPatches);

      this.assertValidPatches(attachmentPatches, FaultOperationPathEnum.groupAttachment);
      const attachmentUid = attachmentPatches[0].oUid;
      // const groupIdInternal = attachmentPatches[0].gId || groupId;

      faultMap.forEach((fUid, aUid) => {
        items.push({faultUid: fUid, activityUid: aUid, attachmentUid});
      });
    }

    try {
      await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, new SyncState({ status: SyncStatusEnum.Syncing }));

      for (const key of Object.keys(patches)) {
        const attachmentPatches: FaultPatch[] = patches[key];
        const attachmentChangeState = this.patchService.generateChangeStateFromPatches(attachmentPatches);
        if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
          /* upload attachments ... */
          const savedLoadState = await this.syncActivityGroupAttachmentChangeState(mydocmaUid,
                                                                                   groupUid,
                                                                                   attachmentPatches[0].oId,
                                                                                   attachmentPatches[0].oUid,
                                                                                   attachmentChangeState);

          syncResult.activityAttachmentsLoadStates.push(savedLoadState);

          const loadItems = items.filter(f => f.attachmentUid === attachmentPatches[0].oUid);
          await this.dbService.saveLoadStates(mydocmaUid, groupUid, loadItems, savedLoadState);
        }
      }

      syncResult.syncState = new SyncState({ status: SyncStatusEnum.Synced });
      await this.patchService.remove(mydocmaUid, allPatches.map(p => p.operation.id));
      await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, syncResult.syncState);
      return syncResult;
    } catch (err) {
      console.error(err);

      // if (err.error.Code === '1000017') { // Todo: add lock flag on Attachment to avoid error
      //   this.notifactionService.error( await  this.translateService.get('MM.fault.lockedAttachment').toPromise());
      //   await this.patchService.remove(mydocmaUid, allPatches.map(p => p.operation.id));
      //   return;
      // }

      const syncStateErr = await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, new SyncState({ status: SyncStatusEnum.Error }));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  private assertValidPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {
    if (!patches && patches.length === 0) {
      throw new Error(`Can't sync attachment. Sync patches are empty.`);
    }
    const faultUid = patches[0].fUid;
    const oUid = patches[0].oUid;
    for (const p of patches) {
      if (p.fUid !== faultUid || p.oUid !== oUid || p.operation.path !== path) {
        throw new Error(`Can't sync attachment ${faultUid}/${oUid}. Sync patches not valid`);
      }
    }
  }
  private async syncFaultAttachmentChangeState(mydocmaUid: string, newFile: File, faultId: any, attachmentId: any,
                                               changeState: ChangeState<AttachmentData>): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.attachmentApiService.addAttachmentToFault(faultId, changeState.data, newFile).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private async syncActivityAttachmentChangeState(mydocmaUid: string, newFile: File, activityId: any, attachmentId: any,
                                                  changeState: ChangeState<AttachmentData>): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.attachmentApiService.addAttachmentToActivity(activityId, changeState.data, newFile).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private async syncActivityGroupAttachmentChangeState(mydocmaUid: string, groupUid: string, attachmentId: number, attachmentUid: string,
                                                       changeState: ChangeState<AttachmentData>): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const file = await this.getAttachmentFile(mydocmaUid, changeState.data.UniqueId);
      if (!file) {
        throw new Error(`Can't sync new attachment ${changeState.data.UniqueId}. Sync file not found`);
      }
      const loadedData = await this.attachmentApiService.addAttachmentToGroup(groupUid, changeState.data, file).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private async getAttachmentFile(mydocmaUid: string, attachmentUid: string): Promise<File> {
    const entity = await this.syncFileService.getFile(mydocmaUid, attachmentUid);
    if (!entity) {
      return null;
    }
    return mapFileEntityToFile(entity);
  }
}
